import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import "./JudgmentDashboard.css";
import { useAuth } from "../App";
import JudgmentUploadModal from "./JudgmentUploadModal";

export default function JudgmentDashboard() {

  const navigate = useNavigate();
  const { hasPermission } = useAuth();

  const [judgments, setJudgments] = useState([]);
  const [loading, setLoading] = useState(true);

  const [search, setSearch] = useState("");

  const [sortField, setSortField] = useState("CreatedAt");
  const [sortDir, setSortDir] = useState("desc");

  const [showModal, setShowModal] = useState(false);
  const [mode, setMode] = useState("create");
  const [editJudgment, setEditJudgment] = useState(null);

  /* ADVANCED SEARCH */

  const [judgeSearch, setJudgeSearch] = useState("");
  const [actSearch, setActSearch] = useState("");
  const [sectionSearch, setSectionSearch] = useState("");
  const [headnoteSearch, setHeadnoteSearch] = useState("");
  const [journalSearch, setJournalSearch] = useState("");
  const [caseTypeSearch, setCaseTypeSearch] = useState("");

  const [citationSearch, setCitationSearch] = useState("");
  const [yearSearch, setYearSearch] = useState("");
  const [volumeSearch, setVolumeSearch] = useState("");
  const [pageSearch, setPageSearch] = useState("");
  const [judgmentDateSearch, setJudgmentDateSearch] = useState("");

  const filtersActive =
    search ||
    citationSearch ||
    yearSearch ||
    volumeSearch ||
    pageSearch ||
    judgmentDateSearch ||
    judgeSearch ||
    actSearch ||
    sectionSearch ||
    headnoteSearch ||
    journalSearch ||
    caseTypeSearch;

  /* FETCH */

  useEffect(() => {

    const timer = setTimeout(() => {

      const url = search
        ? `/api/judgments?q=${encodeURIComponent(search)}`
        : "/api/judgments";

      fetch(url)
        .then(res => res.json())
        .then(data => {
          setJudgments(Array.isArray(data) ? data : []);
        })
        .finally(() => setLoading(false));

    }, 350);

    return () => clearTimeout(timer);

  }, [search]);

  /* DELETE */

  const del = async (id) => {

    if (!window.confirm("Delete this judgment?"))
      return;

    await fetch(`/api/judgments/${id}`, {
      method: "DELETE"
    });

    setJudgments(list =>
      list.filter(j => j.Id !== id)
    );
  };

  function clearFilters() {

    setSearch("");

    setJudgeSearch("");
    setActSearch("");
    setSectionSearch("");
    setHeadnoteSearch("");
    setJournalSearch("");
    setCaseTypeSearch("");
    setCitationSearch("");
    setYearSearch("");
    setVolumeSearch("");
    setPageSearch("");
    setJudgmentDateSearch("");

  }

  /* SEARCH + SORT */



  const filteredJudgments = useMemo(() => {

    /* When backend FULLTEXT search is active,
       do NOT filter again in frontend */

    if (search) {
      return judgments;
    }

    const q = search.toLowerCase();

    return judgments
      .filter(j => {

        const globalMatch =
          !search ||
          [
            (j.Citation || "").replace(/[().]/g, ""),
            j.Judges,
            j.JournalShortName,
            j.VolumeNo,
            j.PageNo,
            j.Headnotes,
            j.JudgmentDate,
            j.CreatedByUid,
            j.SectionNumbers,
            j.Acts
          ]
            .join(" ")
            .toLowerCase()
            .includes(q);

        const judgeMatch =
          !judgeSearch ||
          (j.Judges || "")
            .toLowerCase()
            .includes(judgeSearch.toLowerCase());

        const actMatch =
          !actSearch ||
          (j.Acts || "")
            .toLowerCase()
            .includes(actSearch.toLowerCase());

        const sectionMatch =
          !sectionSearch ||
          (j.SectionNumbers || "")
            .toLowerCase()
            .includes(sectionSearch.toLowerCase());

        const headnoteMatch =
          !headnoteSearch ||
          (j.Headnotes || "")
            .toLowerCase()
            .includes(headnoteSearch.toLowerCase());

        const journalMatch =
          !journalSearch ||
          (j.JournalShortName || "")
            .toLowerCase()
            .includes(journalSearch.toLowerCase());

        const caseTypeMatch =
          !caseTypeSearch ||
          (j.CaseType || "")
            .toLowerCase()
            .includes(caseTypeSearch.toLowerCase());
        const citationMatch =
          !citationSearch ||
          (j.Citation || "")
            .toLowerCase()
            .includes(citationSearch.toLowerCase());

        const yearMatch =
          !yearSearch ||
          String(j.YearPublished || "")
            .toLowerCase()
            .includes(yearSearch.toLowerCase());

        const volumeMatch =
          !volumeSearch ||
          (j.VolumeNo || "")
            .toLowerCase()
            .includes(volumeSearch.toLowerCase());

        const pageMatch =
          !pageSearch ||
          String(j.PageNo || "")
            .toLowerCase()
            .includes(pageSearch.toLowerCase());

        const judgmentDateMatch =
          !judgmentDateSearch ||
          (j.JudgmentDate || "")
            .toLowerCase()
            .includes(judgmentDateSearch.toLowerCase());



        return (
          globalMatch &&
          citationMatch &&
          yearMatch &&
          volumeMatch &&
          pageMatch &&
          judgmentDateMatch &&
          judgeMatch &&
          actMatch &&
          sectionMatch &&
          headnoteMatch &&
          journalMatch &&
          caseTypeMatch
        );

      })

      .sort((a, b) => {

        const x = a[sortField] || "";
        const y = b[sortField] || "";

        if (x < y) return sortDir === "asc" ? -1 : 1;
        if (x > y) return sortDir === "asc" ? 1 : -1;
        return 0;

      });

  }, [
    judgments,
    search,
    citationSearch,
    yearSearch,
    volumeSearch,
    pageSearch,
    judgmentDateSearch,
    judgeSearch,
    actSearch,
    sectionSearch,
    headnoteSearch,
    journalSearch,
    caseTypeSearch,
    sortField,
    sortDir
  ]);

  if (loading)
    return <div className="judgment-loading">Loading judgments…</div>;

  function highlight(text, term) {

    if (!text) return "";
    if (!term) return text;

    const escaped = term.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

    const regex = new RegExp(`(${escaped})`, "gi");

    return text.replace(regex, "<mark>$1</mark>");
  }

  return (

    <div className="judgment-dashboard">

      {/* HEADER */}

      <div className="judgment-header">

        <h2>Judgment Library</h2>

        {hasPermission("judgement", "create") && (

          <button
            className="add-btn"
            data-tooltip="Upload a new judgment"
            // onClick={() => navigate("/judgment/add")}
            onClick={() => {
              setMode("create");
              setEditJudgment(null);
              setShowModal(true);
            }}
          >
            ＋ Add Judgment
          </button>

        )}

      </div>

      {/* TOOLBAR */}

      <div className="judgment-toolbar">

        <input
          type="search"
          placeholder="Search in all fields"
          value={search}
          data-tooltip="Search in all fields"
          onChange={e => setSearch(e.target.value)}
        />

        <input
          type="search"
          placeholder="Judge / Petitioners / Respondents"
          value={judgeSearch}
          onChange={e => setJudgeSearch(e.target.value)}
        />

        <input
          type="search"
          placeholder="Acts"
          value={actSearch}
          onChange={e => setActSearch(e.target.value)}
        />

        <input
          type="search"
          placeholder="Section Numbers"
          value={sectionSearch}
          onChange={e => setSectionSearch(e.target.value)}
        />

        <input
          type="search"
          placeholder="Headnotes"
          value={headnoteSearch}
          onChange={e => setHeadnoteSearch(e.target.value)}
        />

        <input
          type="search"
          placeholder="Journal Short Name"
          value={journalSearch}
          onChange={e => setJournalSearch(e.target.value)}
        />

        <input
          type="search"
          placeholder="Case Type"
          value={caseTypeSearch}
          onChange={e => setCaseTypeSearch(e.target.value)}
        />
        <input
          type="search"
          placeholder="Citation"
          value={citationSearch}
          onChange={e => setCitationSearch(e.target.value)}
        />

        <input
          type="search"
          placeholder="Year Published"
          value={yearSearch}
          onChange={e => setYearSearch(e.target.value)}
        />

        <input
          type="search"
          placeholder="Volume No"
          value={volumeSearch}
          onChange={e => setVolumeSearch(e.target.value)}
        />

        <input
          type="search"
          placeholder="Page No"
          value={pageSearch}
          onChange={e => setPageSearch(e.target.value)}
        />

        <input
          type="search"
          placeholder="Judgment Date"
          value={judgmentDateSearch}
          onChange={e => setJudgmentDateSearch(e.target.value)}
        />
        <div className="sort-group">

          <select
            value={sortField}
            data-tooltip="Sort by field"
            onChange={e => setSortField(e.target.value)}
          >
            <option value="CreatedAt">Upload Date</option>
            <option value="Citation">Citation</option>
            <option value="JournalShortName">Journal</option>
            <option value="JudgmentDate">Judgment Date</option>
          </select>

          <button
            className="toolbar-sort-btn"
            data-tooltip="Toggle sort direction"
            onClick={() => setSortDir(d => (d === "asc" ? "desc" : "asc"))}
          >
            {sortDir === "asc" ? "↑" : "↓"}
          </button>

        </div>

        {filtersActive && (
          <button
            className="clear-btn"
            data-tooltip="Clear all search filters"
            onClick={clearFilters}
          >
            Clear Filters
          </button>
        )}

      </div>

      {/* GRID */}

      <div className="judgment-grid">

        {filteredJudgments.map(j => (

          <div className="judgment-card" key={j.Id}>

            {/* <div className="thumb">
              <span>PDF</span>
            </div> */}

            <h4
              dangerouslySetInnerHTML={{
                __html: highlight(j.Citation, search)
              }}
            />

            <p
              className="subtitle"
              dangerouslySetInnerHTML={{
                __html: highlight(j.CaseType, search)
              }}
            />

            {/* Judges */}

            {j.Judges && (
              <p className="judges">
                {j.Judges}
              </p>
            )}

            {/* Acts */}

            {j.Acts && (
              <p className="acts">
                {j.Acts}
              </p>
            )}

            <p
              className="desc"
              dangerouslySetInnerHTML={{
                __html: highlight(
                  (j.Headnotes || "").slice(0, 120),
                  search
                )
              }}
            />

            {/* ACTIONS */}

            <div className="card-actions">

              <button
                data-tooltip="View judgment"
                onClick={() => navigate(`/judgments/${j.Id}`)}
              >
                View
              </button>

              {hasPermission("judgement", "update") && (

                <button
                  className="edit"
                  data-tooltip="Edit judgment"
                  // onClick={() => navigate(`/judgments/edit/${j.Id}`)}
                  onClick={async () => {
                    try {
                      const res = await fetch(`/api/judgments/${j.Id}`);
                      const fullData = await res.json();

                      setEditJudgment(fullData);   // ✅ full object now
                      setMode("edit");
                      setShowModal(true);

                    } catch (err) {
                      console.error("Failed to load judgment:", err);
                      alert("Unable to load judgment for editing");
                    }
                  }}

                >
                  Edit
                </button>

              )}

              {hasPermission("judgement", "delete") && (

                <button
                  className="danger"
                  data-tooltip="Delete judgment"
                  onClick={() => del(j.Id)}
                >
                  Delete
                </button>

              )}

            </div>

          </div>

        ))}

        {filteredJudgments.length === 0 && (
          <div className="empty">No judgments found</div>
        )}

      </div>
      {showModal && (
        <JudgmentUploadModal
          mode={mode}
          judgmentData={editJudgment}
          onClose={() => setShowModal(false)}
          onJudgmentSaved={() => {
            setShowModal(false);

            // 🔁 Reload judgments (IMPORTANT)
            fetch("/api/judgments")
              .then(res => res.json())
              .then(data => setJudgments(Array.isArray(data) ? data : []));
          }}
        />
      )}
    </div>

  );
}
